import FormProvider from '@/components/HookForm/FormProvider';
import { Stack, Typography } from '@mui/material';
import Label from '@/components/@IntranetLibrary/Label';
import {
  TextFieldButton,
  TextFieldWithButton,
} from '@/components/MenuDrawer/MyMenuDrawer/SubMenu/CertificateOfEmployment/style';
import { FormGroup } from '@/components/Board/CreateBoardModal/BoardForm/BoardFormField/style';
import React, { forwardRef, useImperativeHandle, useState } from 'react';
import useSmsVerificationForm, {
  _smsVerification,
  SmsVerification,
} from '@/hooks/form/useSmsVerificationForm';
import { useCertifySmsCode, useGetCertifyCode } from '@/hooks/apiHooks';
import { useSnackbar } from '@/hooks/global-tools';
import { useTranslation } from 'react-i18next';

export type SmsVerificationFormRef = {
  reset: () => void;
};

type SmsVerificationFormProps = {
  phoneNumber?: string;
  onVerify: (smsVerification: SmsVerification) => void;
};

type LoadingTarget = 'requestCertifyCode' | 'verifyCode';

const SmsVerificationForm = forwardRef<
  SmsVerificationFormRef,
  SmsVerificationFormProps
>((props, ref) => {
  const { t } = useTranslation();
  const { phoneNumber, onVerify } = props;
  const [isVerified, setIsVerified] = useState(false);
  const [isLoadings, setIsLoadings] = useState(new Set<LoadingTarget>());
  const { showSuccess, showError } = useSnackbar();

  const methods = useSmsVerificationForm(phoneNumber);
  const {
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { isValid },
  } = methods;

  const codeOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(
      'verificationCode',
      e.target.value.trim().replace(/\D/g, '').slice(0, 6),
      { shouldValidate: true },
    );
  };

  const requestCertifyCode = async () => {
    if (isLoadings.has('requestCertifyCode')) return;
    isLoadings.add('requestCertifyCode');

    try {
      const {
        data: { smsVerificationId },
      } = await useGetCertifyCode(getValues('phoneNumber'));
      setValue('verificationId', smsVerificationId);
      showSuccess({ message: '인증번호가 전송되었습니다.' });
    } catch (error: any) {
      showError({ message: error.message || '인증번호 전송에 실패했습니다.' });
    }

    isLoadings.delete('requestCertifyCode');
  };

  const verifyCode = () => {
    if (isLoadings.has('verifyCode')) return;
    isLoadings.add('verifyCode');

    handleSubmit(async (data) => {
      try {
        await useCertifySmsCode({
          smsVerificationId: data.verificationId,
          smsVerificationCode: data.verificationCode,
        });
        setIsVerified(true);
        onVerify(data);
      } catch (error: any) {
        let message = error.message || t('인증에 실패했습니다.');
        if (error.status === 422) {
          message = t('인증번호가 일치하지 않습니다.');
        }
        showError({ message });
      }
      isLoadings.delete('verifyCode');
    })();
  };

  useImperativeHandle(ref, () => ({
    reset: () => {
      setIsLoadings(new Set<LoadingTarget>());
      setIsVerified(false);
      reset({ ..._smsVerification, phoneNumber });
    },
  }));

  return (
    <FormProvider methods={methods} id={'SmsVerificationForm'}>
      <Stack sx={{ gap: '24px' }}>
        <FormGroup gapsize={'s'}>
          <Label isRequired={true} title={t('휴대폰 번호')} size={14} />
          <Stack direction={'row'} gap={'8px'}>
            <TextFieldWithButton
              name={'phoneNumber'}
              variant={'outlined'}
              disabled={true}
            />
            <TextFieldButton
              onClick={requestCertifyCode}
              disabled={!getValues('phoneNumber') || isVerified}
              loading={isLoadings.has('requestCertifyCode')}
              sx={{ width: '88px', paddingX: '0' }}
            >
              {getValues('verificationId') !== ''
                ? t('재전송')
                : t('인증번호 전송')}
            </TextFieldButton>
          </Stack>
        </FormGroup>

        <FormGroup gapsize={'s'}>
          <Label isRequired={true} title={t('인증번호')} size={14} />
          <Stack direction={'row'} gap={'8px'}>
            <TextFieldWithButton
              name={'verificationCode'}
              helperText={
                isVerified && (
                  <Typography sx={{ color: '#43B16F', fontSize: '12px' }}>
                    {t('인증되었습니다.')}
                  </Typography>
                )
              }
              disabled={!getValues('verificationId') || isVerified}
              variant={'outlined'}
              placeholder={t('인증번호 {{n}}자리', { n: 6 })}
              onChange={codeOnChange}
            />
            <TextFieldButton
              onClick={verifyCode}
              disabled={!isValid || isVerified}
              loading={isLoadings.has('verifyCode')}
              sx={{ width: '88px', paddingX: '0' }}
            >
              {t('인증')}
            </TextFieldButton>
          </Stack>
        </FormGroup>
      </Stack>
    </FormProvider>
  );
});
SmsVerificationForm.displayName = 'SmsVerificationForm';

export default SmsVerificationForm;
